import {useRef, useState} from 'react';
import {EditorState, RichUtils} from 'draft-js';
import {
  MdFormatBold,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatListNumbered,
  MdFormatUnderlined,
  MdLink,
  MdStrikethroughS,
} from 'react-icons/md';
import styled from 'styled-components';

const Toolbar = styled.div`
  position: relative;
  display: flex;
  min-width: 0;
  align-items: center;
  gap: 4px;
`;

const ToolButton = styled.button<{$active?: boolean}>`
  display: inline-flex;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  align-items: center;
  justify-content: center;
  border: 1px solid
    ${({$active, theme}) => ($active ? theme.colors.purple300 : 'transparent')};
  border-radius: 6px;
  background: ${({$active, theme}) =>
    $active ? theme.colors.backgroundDark : theme.colors.white};
  color: ${({$active, theme}) =>
    $active ? theme.colors.purple300 : theme.colors.textBody};
  transition: background 120ms ease, border-color 120ms ease, color 120ms ease;

  &:hover:not(:disabled) {
    border-color: ${({theme}) => theme.colors.borderDark};
    background: ${({theme}) => theme.colors.backgroundLight};
    color: ${({theme}) => theme.colors.blackTwo};
  }

  &:focus-visible {
    outline: 2px solid ${({theme}) => theme.colors.purple300};
    outline-offset: 1px;
  }

  &:disabled {
    cursor: not-allowed;
    color: ${({theme}) => theme.colors.textMuted};
  }
`;

const Separator = styled.span`
  width: 1px;
  height: 20px;
  margin: 0 4px;
  background: ${({theme}) => theme.colors.borderLight};
`;

const LinkPopover = styled.form`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  display: grid;
  width: min(320px, calc(100vw - 64px));
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 8px;
  padding: 10px;
  border: 1px solid ${({theme}) => theme.colors.borderDark};
  border-radius: 8px;
  background: ${({theme}) => theme.colors.white};
  box-shadow: 0 8px 24px rgba(30, 30, 47, 0.12);

  input {
    min-width: 0;
    height: 36px;
    padding: 0 10px;
    border: 1px solid ${({theme}) => theme.colors.borderDark};
    border-radius: 6px;
    color: ${({theme}) => theme.colors.blackTwo};
    font-size: 14px;
    font-weight: 400;
    outline: none;

    &::placeholder {
      color: ${({theme}) => theme.colors.textBody};
    }

    &:focus {
      border-color: ${({theme}) => theme.colors.purple300};
    }
  }

  button {
    height: 36px;
    padding: 0 12px;
    border-radius: 6px;
    background: ${({theme}) => theme.colors.purple300};
    color: ${({theme}) => theme.colors.alwaysWhite};
    font-size: 14px;
    font-weight: 600;
  }
`;

interface RichTextToolbarProps {
  editorState: EditorState;
  onChange: (editorState: EditorState) => void;
  disabled?: boolean;
  className?: string;
  showUnderline?: boolean;
  showStrikethrough?: boolean;
  showLink?: boolean;
}

export const RichTextToolbar = ({
  editorState,
  onChange,
  disabled = false,
  className,
  showUnderline = false,
  showStrikethrough = false,
  showLink = true,
}: RichTextToolbarProps) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkValue, setLinkValue] = useState('');
  const savedSelection = useRef(editorState.getSelection());
  const inlineStyles = editorState.getCurrentInlineStyle();
  const blockType = RichUtils.getCurrentBlockType(editorState);
  const linkDisabled = disabled || editorState.getSelection().isCollapsed();

  const toggleInline = (
    style: 'BOLD' | 'ITALIC' | 'UNDERLINE' | 'STRIKETHROUGH',
  ) => onChange(RichUtils.toggleInlineStyle(editorState, style));

  const toggleBlock = (type: 'unordered-list-item' | 'ordered-list-item') =>
    onChange(RichUtils.toggleBlockType(editorState, type));

  const openLink = () => {
    if (linkDisabled) return;
    savedSelection.current = editorState.getSelection();
    setLinkValue('');
    setLinkOpen(true);
  };

  const addLink = (event: React.FormEvent) => {
    event.preventDefault();
    const url = linkValue.trim();
    if (!url) return;

    const selectionState = savedSelection.current;
    let nextState = EditorState.forceSelection(editorState, selectionState);
    const contentWithEntity = nextState
      .getCurrentContent()
      .createEntity('LINK', 'MUTABLE', {url});
    const entityKey = contentWithEntity.getLastCreatedEntityKey();
    nextState = EditorState.set(nextState, {
      currentContent: contentWithEntity,
    });
    onChange(RichUtils.toggleLink(nextState, selectionState, entityKey));
    setLinkOpen(false);
    setLinkValue('');
  };

  const preserveSelection = (event: React.MouseEvent) => {
    event.preventDefault();
  };

  return (
    <Toolbar
      className={className}
      role="toolbar"
      aria-label="Text formatting"
      onMouseDown={preserveSelection}>
      <ToolButton
        type="button"
        aria-label="Bold"
        title="Bold"
        $active={inlineStyles.has('BOLD')}
        disabled={disabled}
        onClick={() => toggleInline('BOLD')}>
        <MdFormatBold aria-hidden size={19} />
      </ToolButton>
      <ToolButton
        type="button"
        aria-label="Italic"
        title="Italic"
        $active={inlineStyles.has('ITALIC')}
        disabled={disabled}
        onClick={() => toggleInline('ITALIC')}>
        <MdFormatItalic aria-hidden size={19} />
      </ToolButton>
      {showUnderline && (
        <ToolButton
          type="button"
          aria-label="Underline"
          title="Underline"
          $active={inlineStyles.has('UNDERLINE')}
          disabled={disabled}
          onClick={() => toggleInline('UNDERLINE')}>
          <MdFormatUnderlined aria-hidden size={19} />
        </ToolButton>
      )}
      {showStrikethrough && (
        <ToolButton
          type="button"
          aria-label="Strikethrough"
          title="Strikethrough"
          $active={inlineStyles.has('STRIKETHROUGH')}
          disabled={disabled}
          onClick={() => toggleInline('STRIKETHROUGH')}>
          <MdStrikethroughS aria-hidden size={19} />
        </ToolButton>
      )}
      <Separator aria-hidden />
      <ToolButton
        type="button"
        aria-label="Bulleted list"
        title="Bulleted list"
        $active={blockType === 'unordered-list-item'}
        disabled={disabled}
        onClick={() => toggleBlock('unordered-list-item')}>
        <MdFormatListBulleted aria-hidden size={20} />
      </ToolButton>
      <ToolButton
        type="button"
        aria-label="Numbered list"
        title="Numbered list"
        $active={blockType === 'ordered-list-item'}
        disabled={disabled}
        onClick={() => toggleBlock('ordered-list-item')}>
        <MdFormatListNumbered aria-hidden size={20} />
      </ToolButton>
      {showLink && (
        <ToolButton
          type="button"
          aria-label="Add link"
          title={linkDisabled ? 'Select text to add a link' : 'Add link'}
          disabled={linkDisabled}
          onClick={openLink}>
          <MdLink aria-hidden size={19} />
        </ToolButton>
      )}
      {showLink && linkOpen && (
        <LinkPopover
          onSubmit={addLink}
          onMouseDown={(event) => event.stopPropagation()}>
          <input
            autoFocus
            type="url"
            aria-label="Link URL"
            placeholder="https://example.com"
            value={linkValue}
            onChange={(event) => setLinkValue(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setLinkOpen(false);
            }}
          />
          <button type="submit">Add</button>
        </LinkPopover>
      )}
    </Toolbar>
  );
};
